import {
  action,
  computed,
  makeObservable,
  observable,
  onBecomeObserved,
} from "mobx"

import { ScopesCollection } from "@src/pages/settingsApiKeys/classes/ScopesCollection"
import { catchErrors, tokensApi } from "@src/shared/api"
import { Pagination } from "@src/shared/api/generated"
import { CollectionWithPagination } from "@src/shared/classes/Collection/CollectionWithPagination"
import { loading } from "@src/shared/classes/utils/loading"
import { wrapSingleton } from "@src/shared/classes/utils/wrapSingleton"

export type TokenType = {
  created_at: string
  id: string
  name: string
  scopes: string[]
}

export type TokenCreateRequestType = {
  name: string
  scopes?: string[]
}

type TokensCollectionLoadType = {
  page?: number
}

export class TokensCollection extends loading(
  ["fetch", "delete", "create"],
  CollectionWithPagination<TokenType>,
) {
  scopes = new ScopesCollection()
  visibleTokensId: string[] = []

  constructor() {
    super()

    makeObservable(this, {
      create: action.bound,
      delete: action.bound,
      fetch: action.bound,
      isLoadStatus: computed,
      scopes: observable.deep,
      toggleIsVisibleId: action.bound,
      visibleTokensId: observable,
    })

    const kill = onBecomeObserved(this, "isLoading", () => {
      kill()

      return this.load()
    })
  }

  async create(req: TokenCreateRequestType) {
    const request: TokenCreateRequestType = {
      ...req,
      scopes:
        req.scopes?.length === this.scopes.flatScopes.length
          ? undefined
          : req.scopes,
    }

    return tokensApi.createTokens(request).catch(catchErrors).then(this.load)
  }

  async delete(id: string) {
    return tokensApi.deleteTokens(id).catch(catchErrors).then(this.load)
  }

  async fetch(req: TokensCollectionLoadType) {
    try {
      const { data: { data } = {} } = await tokensApi.getTokens(
        undefined,
        this.calcOffset(req.page),
        this.pageSize,
      )

      if (data) {
        const { pagination, tokens } = data as any

        return {
          data: tokens as TokenType[],
          pagination: pagination as Pagination,
        }
      }
    } catch (e: any) {
      catchErrors(e)
    }

    return null
  }

  isVisibleId(id: string) {
    return this.visibleTokensId.includes(id)
  }

  toggleIsVisibleId(id: string) {
    const included = this.visibleTokensId.includes(id)

    if (included) {
      this.visibleTokensId = this.visibleTokensId.filter((i) => i !== id)
    } else {
      this.visibleTokensId.push(id)
    }
  }

  get isLoadStatus() {
    return this.isLoading || this.scopes.isLoading
  }
}

export const GlobalTokensCollection = wrapSingleton(TokensCollection)
